import { useState } from "react";
import {
  Package,
  Edit,
  Plus,
  Building2,
  ShoppingCart,
  FileText,
  Settings,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/Section";
import { InfoField, InfoFieldGroup } from "@/components/ui/info-field";
import {
  DetailPageLayout,
} from "@/components/ui/detail-page-layout";
import { DetailPageSkeleton } from "@/components/ui/detail-page-skeleton";
import { NotFoundState } from "@/components/ui/not-found-state";
import { MoneyInput } from "@/components/ui/money-input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  SlideDrawer,
} from "@/components/ui/slide-drawer";
import { FormDrawer } from "@/components/ui/form-drawer";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, type StatusType } from "@/components/StatusBadge";
import { AiSourceBadge } from "@/components/ai-source-badge";
import { EntityCodeBadge } from "@/components/entity-code-badge";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from "@/components/theme-provider";

/**
 * Internal Design System smoke route
 *
 * Mounted at `/internal/design` for developers + designers to spot-check
 * every primitive renders correctly in both themes. Not user-facing — there
 * is no link to it from the sidebar. Reachable only by typing the URL.
 *
 * This page exists per ianfiles/ui-design-system.md §8.3 and complements:
 *   - `scripts/check-design-system.mjs` (drift detector)
 *   - `docs/design-system-page-migration.md` (per-page migration recipes)
 *
 * If a primitive renders wrong here, fix it in the primitive's source file,
 * not on this page.
 */

const statusTypes: Array<{
  type: StatusType;
  label: string;
  examples: string[];
}> = [
  { type: "account", label: "Account", examples: ["active", "inactive", "prospect"] },
  {
    type: "quote",
    label: "Quote",
    examples: ["draft", "sent", "accepted", "converted", "rejected", "expired"],
  },
  {
    type: "order",
    label: "Order",
    examples: [
      "pending",
      "submitted",
      "confirmed",
      "partially_fulfilled",
      "fulfilled",
      "partially_shipped",
      "fully_shipped",
      "partially_delivered",
      "fully_delivered",
      "invoiced",
      "closed",
      "cancelled",
    ],
  },
  {
    type: "purchaseOrder",
    label: "Purchase Order",
    examples: [
      "draft",
      "submitted",
      "acknowledged",
      "confirmed",
      "in_production",
      "partially_received",
      "received",
      "closed",
      "cancelled",
      "issue",
    ],
  },
  {
    type: "invoice",
    label: "Invoice",
    examples: [
      "draft",
      "sent",
      "deposit_requested",
      "overdue",
      "partially_paid",
      "paid",
      "voided",
      "closed",
    ],
  },
  {
    type: "shipment",
    label: "Shipment",
    examples: [
      "PLANNED",
      "BOOKED",
      "IN_TRANSIT",
      "DELIVERED",
      "EXCEPTION",
      "CANCELLED",
    ],
  },
  {
    type: "opportunity",
    label: "Opportunity",
    examples: [
      "prospecting",
      "qualification",
      "proposal",
      "negotiation",
      "closed_won",
      "closed_lost",
    ],
  },
  {
    type: "product",
    label: "Product",
    examples: ["stock", "custom"],
  },
  {
    type: "contract",
    label: "Contract",
    examples: ["draft", "active", "expired", "terminated"],
  },
  {
    type: "procurement",
    label: "Procurement",
    examples: [
      "not_required",
      "pending_po",
      "po_submitted",
      "po_confirmed",
      "in_production",
      "ready_to_ship",
      "partially_shipped",
      "fully_shipped",
      "issue",
    ],
  },
  {
    type: "fulfillment",
    label: "Fulfillment",
    examples: ["pending", "partial", "allocated", "fulfilled"],
  },
];

const accentColors: Array<{ name: string; semantic: string }> = [
  { name: "blue", semantic: "Primary identity" },
  { name: "emerald", semantic: "Money / metrics" },
  { name: "green", semantic: "Success / fulfilled" },
  { name: "amber", semantic: "Pending / warning" },
  { name: "orange", semantic: "Soft warning" },
  { name: "violet", semantic: "AI / system" },
  { name: "cyan", semantic: "Reference" },
  { name: "purple", semantic: "Premium / contractual" },
  { name: "sky", semantic: "Communication" },
  { name: "slate", semantic: "Neutral / archive" },
  { name: "yellow", semantic: "Soft attention" },
  { name: "indigo", semantic: "Specialised" },
  { name: "rose", semantic: "Brand identity" },
  { name: "teal", semantic: "Operations" },
];

export default function DesignSystemPage() {
  const { resolvedTheme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [slideDrawerOpen, setSlideDrawerOpen] = useState(false);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formPending, setFormPending] = useState(false);
  const [searchableValue, setSearchableValue] = useState<string | undefined>();

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={<span>Internal / Design System</span>}
        title="Design System"
        description={`Smoke test for every primitive. Currently rendering in ${resolvedTheme} mode.`}
        actions={<ThemeToggle />}
      />

      {/* ─── Buttons ─────────────────────────────────────────────── */}
      <Section
        title="Buttons"
        icon={Edit}
        iconColor="text-blue-500"
        accentColor="border-blue-500"
        data-testid="section-design-buttons"
      >
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="default">Default</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="default">Default size</Button>
            <Button size="sm">sm size</Button>
            <Button size="lg">lg size</Button>
            <Button size="icon" variant="ghost" aria-label="Edit">
              <Edit className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" aria-label="Add">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Section>

      {/* ─── Badges ──────────────────────────────────────────────── */}
      <Section
        title="Badges (shadcn variants)"
        icon={Package}
        iconColor="text-violet-500"
        accentColor="border-violet-500"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="default">Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="destructive">Destructive</Badge>
          <Badge variant="outline">Outline</Badge>
          <AiSourceBadge source="ai_extract" />
          <AiSourceBadge source="import" />
          <AiSourceBadge source="system" />
          <EntityCodeBadge code="ACME-001" />
        </div>
      </Section>

      {/* ─── StatusBadge (full Appendix B taxonomy) ───────────────── */}
      <Section
        title="StatusBadge — full canonical taxonomy"
        icon={FileText}
        iconColor="text-emerald-500"
        accentColor="border-emerald-500"
      >
        <div className="space-y-3">
          {statusTypes.map((row) => (
            <div key={row.type} className="space-y-1">
              <div className="text-xs text-muted-foreground">{row.label}</div>
              <div className="flex flex-wrap gap-1.5">
                {row.examples.map((status) => (
                  <StatusBadge key={status} status={status} type={row.type} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── Section accent palette ──────────────────────────────── */}
      <Section
        title="Section accent palette (Appendix A)"
        icon={TrendingUp}
        iconColor="text-cyan-500"
        accentColor="border-cyan-500"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {accentColors.map((c) => (
            <div
              key={c.name}
              className={`bg-card border border-card-border rounded-r-lg shadow-sm border-l-[3px] border-${c.name}-500 px-3 py-2`}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <span
                  className={`inline-block h-2 w-2 rounded-full bg-${c.name}-500`}
                />
                {c.name}-500
              </div>
              <div className="text-xs text-muted-foreground">{c.semantic}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── Inputs ──────────────────────────────────────────────── */}
      <Section
        title="Inputs / Textareas / Selects"
        icon={Edit}
        iconColor="text-orange-500"
        accentColor="border-orange-500"
      >
        <InfoFieldGroup columns={2}>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Input — default
            </label>
            <Input placeholder="Default variant" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Input — flat
            </label>
            <Input variant="flat" placeholder="Flat variant (drawers)" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Textarea — default
            </label>
            <Textarea placeholder="Default textarea" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Textarea — flat
            </label>
            <Textarea variant="flat" placeholder="Flat textarea (drawers)" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Select — default
            </label>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Pick one…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="a">Option A</SelectItem>
                <SelectItem value="b">Option B</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Select — flat
            </label>
            <Select>
              <SelectTrigger variant="flat">
                <SelectValue placeholder="Pick one…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="a">Option A</SelectItem>
                <SelectItem value="b">Option B</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              MoneyInput
            </label>
            <MoneyInput placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              SearchableSelect
            </label>
            <SearchableSelect
              value={searchableValue}
              onValueChange={setSearchableValue}
              options={[
                { value: "1", label: "Acme Corp", hint: "ACME-001" },
                { value: "2", label: "Globex", hint: "GLX-002" },
                { value: "3", label: "Initech", hint: "INI-003" },
              ]}
              placeholder="Pick an account…"
              allowClear
            />
          </div>
        </InfoFieldGroup>
      </Section>

      {/* ─── Card vs Section ─────────────────────────────────────── */}
      <Section
        title="Card vs Section"
        icon={Building2}
        iconColor="text-sky-500"
        accentColor="border-sky-500"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Card (text-2xl)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Card primitive — used for dashboard KPI tiles + login. Title is
                <code className="mx-1 px-1 py-0.5 rounded bg-muted text-xs">
                  text-2xl
                </code>
                which is deliberately large.
              </p>
            </CardContent>
          </Card>

          <Section
            title="Railless Section"
            icon={Package}
            iconColor="text-muted-foreground"
          >
            <p className="text-sm text-muted-foreground">
              Section without accent rail. Use when there's no semantic colour
              choice — falls back to plain rounded-lg with the card surface.
            </p>
          </Section>
        </div>
      </Section>

      {/* ─── InfoField group ─────────────────────────────────────── */}
      <Section
        title="InfoField + InfoFieldGroup"
        icon={FileText}
        iconColor="text-amber-500"
        accentColor="border-amber-500"
      >
        <InfoFieldGroup columns={3}>
          <InfoField label="Code" value="ACME-001" />
          <InfoField label="Industry" value="Manufacturing" />
          <InfoField label="Empty" value={null} />
          <InfoField label="With hint" value="$24,500" hint="Net 30 terms" />
          <InfoField label="Number" value={0} />
          <InfoField label="Boolean" value={String(false)} />
        </InfoFieldGroup>
      </Section>

      {/* ─── Detail page layout demo ─────────────────────────────── */}
      <Section
        title="DetailPageLayout"
        icon={LayoutIcon}
        iconColor="text-indigo-500"
        accentColor="border-indigo-500"
      >
        <DetailPageLayout>
          <DetailPageLayout.Main>
            <Section title="Main Section A" accentColor="border-blue-500" icon={Package} iconColor="text-blue-500">
              <p className="text-sm text-muted-foreground">
                Main column section. 2/3 width on lg+.
              </p>
            </Section>
            <Section title="Main Section B" accentColor="border-emerald-500" icon={ShoppingCart} iconColor="text-emerald-500">
              <p className="text-sm text-muted-foreground">
                Second main column section.
              </p>
            </Section>
          </DetailPageLayout.Main>
          <DetailPageLayout.Side>
            <Section title="Side Section" accentColor="border-slate-500" icon={Settings} iconColor="text-slate-500">
              <p className="text-sm text-muted-foreground">
                Side column section. 1/3 width on lg+.
              </p>
            </Section>
          </DetailPageLayout.Side>
        </DetailPageLayout>
      </Section>

      {/* ─── Loading + Empty states ──────────────────────────────── */}
      <Section
        title="Loading + Empty states"
        icon={Package}
        iconColor="text-yellow-500"
        accentColor="border-yellow-500"
        collapsible
        defaultCollapsed
      >
        <div className="space-y-6">
          <div>
            <div className="text-xs text-muted-foreground mb-2">
              DetailPageSkeleton (three-column)
            </div>
            <div className="rounded-md border p-4">
              <DetailPageSkeleton layout="three-column" sectionCount={2} />
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-2">
              NotFoundState
            </div>
            <div className="rounded-md border">
              <NotFoundState entityType="Order" backPath="/orders" description="The order may have been archived." />
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-2">Skeleton</div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      </Section>

      {/* ─── Drawers + dialogs ───────────────────────────────────── */}
      <Section
        title="Drawers + Dialog (Phase 5 swap)"
        icon={Edit}
        iconColor="text-rose-500"
        accentColor="border-rose-500"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
            Open Dialog (renders as drawer)
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSlideDrawerOpen(true)}
          >
            Open SlideDrawer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFormDrawerOpen(true)}
          >
            Open FormDrawer
          </Button>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Dialog (drawer-rendered)</DialogTitle>
              <DialogDescription>
                The Dialog primitive globally renders as a right-side drawer
                per Phase 5. Pass `centered` to opt back into the legacy
                centered modal layout.
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm">
              Notice: outside click is suppressed, so users can't lose form
              state by accident. Click the X to close.
            </p>
          </DialogContent>
        </Dialog>

        <SlideDrawer
          open={slideDrawerOpen}
          onOpenChange={setSlideDrawerOpen}
          title="SlideDrawer demo"
          description="Opinionated floating right Sheet."
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              SlideDrawer body. Use this as the surface for non-form panels
              (filter trays, detail previews, etc.).
            </p>
            <Input variant="flat" placeholder="Flat input inside drawer" />
          </div>
        </SlideDrawer>

        <FormDrawer
          open={formDrawerOpen}
          onOpenChange={(open) => {
            setFormDrawerOpen(open);
            if (!open) setFormPending(false);
          }}
          title="FormDrawer demo"
          description="Cancel + Save footer wired via formId."
          formId="design-system-demo-form"
          isPending={formPending}
          submitLabel="Save Changes"
        >
          <form
            id="design-system-demo-form"
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              setFormPending(true);
              setTimeout(() => {
                setFormPending(false);
                setFormDrawerOpen(false);
              }, 1500);
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Name
                </label>
                <Input variant="flat" placeholder="Acme Corp" required />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Code
                </label>
                <Input variant="flat" placeholder="ACME-001" required />
              </div>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-3">
                Pricing
              </p>
              <MoneyInput placeholder="0.00" />
            </div>
          </form>
        </FormDrawer>
      </Section>
    </div>
  );
}

// Tiny inline icon so we don't pull a separate lucide name twice.
function LayoutIcon(props: React.SVGProps<SVGSVGElement>) {
  return <LayoutGrid {...props} />;
}

import { LayoutGrid } from "lucide-react";
